import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Attendee } from '@/types/attendee';
import AttendeesTable from './AttendeesTable';
import { QrScanner } from './scanner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';
import MobileTableBody from './table/mobile/MobileTableBody';
import SearchBar from './table/SearchBar';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { useAdminPageUI } from '@/hooks/admin/useAdminPageUI';
import { useToast } from '@/hooks/use-toast';
import { formatTShirtSize } from '@/utils/formatUtils';

interface AttendeesContentProps {
  totalAttended: number;
  totalRegistered: number;
  attendees: Attendee[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onQrScan: (code: string) => void;
  onCodeSubmit: (code: string) => void;
  onRefreshData?: () => void;
  onDirectUpdate?: (code: string, updates: any) => Promise<any>;
  onSilentUpdate?: (attendee: Attendee) => void;
  lastSearches?: string[];
}

const AttendeesContent: React.FC<AttendeesContentProps> = ({
  totalAttended,
  totalRegistered,
  attendees,
  searchQuery,
  setSearchQuery,
  onQrScan,
  onCodeSubmit,
  onRefreshData,
  onDirectUpdate,
  onSilentUpdate,
  lastSearches = []
}) => {
  const isRefreshing = useRef(false);
  const lastQrScanTime = useRef(0);
  const pendingUpdates = useRef<Record<string, boolean>>({});
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const throttledRefreshTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Track local attendees state for immediate UI updates
  const [localAttendees, setLocalAttendees] = useState<Record<string, Attendee>>({});
  
  const originalTShirtSizes = useRef<Record<string, string>>({});
  
  const { performSearch, elasticResults } = useAdminPageUI();
  
  // Enhanced tracking of original t-shirt sizes
  useEffect(() => {
    attendees.forEach(attendee => {
      if (attendee.code && attendee.t_shirt_size) {
        originalTShirtSizes.current[attendee.code] = attendee.t_shirt_size;
      }
    });
  }, [attendees]);
  
  // Build initial local state from attendees prop
  useEffect(() => {
    const newAttendeeMap: Record<string, Attendee> = {};
    attendees.forEach(attendee => {
      if (attendee.code) {
        newAttendeeMap[attendee.code] = attendee;
      }
    });
    setLocalAttendees(newAttendeeMap);
  }, [attendees]);
  
  useEffect(() => {
    const handleAttendeeCheckedIn = (event: CustomEvent) => {
      if (event.detail && event.detail.attendee) {
        const attendee = event.detail.attendee;
        const wasAlreadyCheckedIn = event.detail.wasAlreadyCheckedIn;
        const useModalOnly = event.detail.useModalOnly === true;
        
        // Update local state for immediate UI feedback
        if (attendee.code) {
          setLocalAttendees(prev => ({
            ...prev,
            [attendee.code]: {
              ...prev[attendee.code],
              ...attendee,
              attended: true,
              checkedInAt: attendee.checkedInAt || new Date().toISOString()
            }
          }));
        }
        
        // We're only using the modal, so no toast notifications for check-ins
        if (useModalOnly || wasAlreadyCheckedIn) {
          console.log('Using modal only for attendee:', attendee.name);
          return;
        }
      }
    };
    
    window.addEventListener('attendee-checked-in', handleAttendeeCheckedIn as EventListener);
    
    return () => {
      window.removeEventListener('attendee-checked-in', handleAttendeeCheckedIn as EventListener);
    };
  }, [toast]);
  
  // Enhanced throttled refresh with safety timeouts
  const handleRefresh = useCallback(() => {
    if (!onRefreshData || isRefreshing.current) return;
    
    console.log('AttendeesContent: Refreshing data due to QR scan or manual refresh');
    isRefreshing.current = true;
    
    // Clear any existing throttled refresh timeout
    if (throttledRefreshTimeout.current) {
      clearTimeout(throttledRefreshTimeout.current);
      throttledRefreshTimeout.current = null;
    }
    
    onRefreshData();
    
    // Set a safety timeout to clear the refreshing flag
    throttledRefreshTimeout.current = setTimeout(() => {
      isRefreshing.current = false;
      throttledRefreshTimeout.current = null;
    }, 1000);
  }, [onRefreshData]);
  
  const enhancedDirectUpdate = useCallback(async (code: string, updates: any) => {
    const originalSize = originalTShirtSizes.current[code];
    
    const enhancedUpdates = {
      ...updates,
      t_shirt_size: updates.t_shirt_size || originalSize
    };
    
    console.log(`AttendeesContent: Enhanced update for ${code} preserving t_shirt_size=${enhancedUpdates.t_shirt_size}`);
    
    // Optimistically update local state for immediate UI response
    if (updates.hasOwnProperty('attended')) {
      const currentAttendee = attendees.find(a => a.code === code);
      if (currentAttendee) {
        const updatedAttendee = {
          ...currentAttendee,
          ...enhancedUpdates,
          attended: updates.attended,
          checkedInAt: updates.attended ? (updates.checkedInAt || new Date().toISOString()) : null
        };
        
        // Update local state immediately
        setLocalAttendees(prev => ({
          ...prev,
          [code]: updatedAttendee
        }));
        
        // If silently updating is available, use it
        if (onSilentUpdate) {
          onSilentUpdate(updatedAttendee);
        }
      }
    }
    
    if (onDirectUpdate) {
      return await onDirectUpdate(code, enhancedUpdates);
    }
    
    return null;
  }, [onDirectUpdate, attendees, onSilentUpdate]);
  
  const handleAttendeeSelect = useCallback((attendee: Attendee) => {
    console.log('Attendee selected from search:', attendee.code);
    
    if (attendee.attended) {
      toast({
        title: "!!! Đã được điểm danh !!!",
        description: `${attendee.name} đã được điểm danh trước đó !!!`,
        variant: "destructive"
      });
      return;
    }
    
    if (onCodeSubmit) {
      onCodeSubmit(attendee.code);
    }
  }, [onCodeSubmit, toast]);
  
  // Improved scheduled refresh function with multiple attempts
  const scheduleFollowUpRefresh = useCallback((delay: number = 500) => {
    setTimeout(() => {
      if (!isRefreshing.current && onRefreshData) {
        console.log('Executing follow-up refresh to ensure data consistency');
        handleRefresh();
      }
    }, delay);
  }, [handleRefresh, onRefreshData]);
  
  useEffect(() => {
    performSearch(searchQuery, attendees);
  }, [searchQuery, attendees, performSearch]);
  
  // Enhanced event handling for UI updates with focus on attendance status changes
  useEffect(() => {
    const handleQrScanConfirmed = (event: CustomEvent) => {
      console.log('Event received in AttendeesContent:', event.type, event.detail);
      
      const attendeeCode = event.detail?.attendeeCode;
      const updatedAttendee = event.detail?.attendee || event.detail?.updatedAttendee;
      const isHighPriority = event.detail?.highPriority === true;
      const requiresRefresh = event.detail?.refreshRequired === true || 
                             event.detail?.forceRefresh === true;
      
      if (updatedAttendee && updatedAttendee.code) {
        console.log(`Processing attendee update for ${updatedAttendee.name} (${updatedAttendee.code})`);
        
        // Update local state for immediate UI response
        setLocalAttendees(prev => ({
          ...prev,
          [updatedAttendee.code]: updatedAttendee
        }));
        
        if (onSilentUpdate) {
          console.log(`Silently updating attendee ${updatedAttendee.code} in parent components`);
          onSilentUpdate(updatedAttendee);
        }
        
        pendingUpdates.current[updatedAttendee.code] = true;
      } 
      else if (attendeeCode) {
        pendingUpdates.current[attendeeCode] = true;
      }
      
      // Track when last refresh happened to prevent too frequent refreshes
      const now = Date.now();
      const shouldRefresh = requiresRefresh || isHighPriority || 
                           now - lastQrScanTime.current > 800;
      
      if (shouldRefresh) {
        lastQrScanTime.current = now;
        
        if (onRefreshData && !isRefreshing.current) {
          console.log('Immediate refresh triggered by', event.type);
          handleRefresh();
          
          // Schedule additional follow-up refreshes for reliability
          scheduleFollowUpRefresh(500);
          scheduleFollowUpRefresh(1500);
          
          // For high priority updates like manual code entry, do an extra refresh
          if (isHighPriority || event.type === 'attendance-ui-update' || 
              event.type === 'global-attendance-update') {
            scheduleFollowUpRefresh(3000);
          }
        }
      } else {
        console.log('Skipping redundant refresh - too soon or not required');
      }
    };
    
    // Enhanced list of events to listen for with prioritization
    const eventTypes = [
      'qr-scan-confirmed', 
      'attendance-confirmed', 
      'database-update-success', 
      'attendance-direct-confirmed',
      'database-direct-update',
      'attendance-ui-update',
      'manual-code-processed',
      'global-attendance-update'
    ];
    
    // Register all event listeners
    eventTypes.forEach(eventType => {
      window.addEventListener(eventType, handleQrScanConfirmed as EventListener);
    });
    
    return () => {
      // Cleanup all event listeners
      eventTypes.forEach(eventType => {
        window.removeEventListener(eventType, handleQrScanConfirmed as EventListener);
      });
      
      // Clear any pending timeouts
      if (throttledRefreshTimeout.current) {
        clearTimeout(throttledRefreshTimeout.current);
        throttledRefreshTimeout.current = null;
      }
    };
  }, [onRefreshData, handleRefresh, scheduleFollowUpRefresh, onSilentUpdate]);
  
  // Listen specifically for the global attendance update event
  useEffect(() => {
    const handleGlobalAttendanceUpdate = (event: CustomEvent) => {
      console.log('Global attendance update received:', event.detail);
      
      if (event.detail?.refreshRequired && onRefreshData && !isRefreshing.current) {
        console.log('Forced refresh from global attendance update');
        handleRefresh();
      }
    };
    
    window.addEventListener('global-attendance-update', handleGlobalAttendanceUpdate as EventListener);
    
    return () => {
      window.removeEventListener('global-attendance-update', handleGlobalAttendanceUpdate as EventListener);
    };
  }, [handleRefresh, onRefreshData]);
  
  const scrollAreaHeight = isMobile 
    ? 'calc(100vh - 270px)'
    : 'calc(100vh - 350px)';
  
  const renderMobileStats = () => (
    <motion.div 
      className="flex justify-between items-center mb-4 px-1"
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 text-sm py-1.5">
        <span className="font-normal text-blue-500 dark:text-blue-400 mr-1">Đã đăng ký:</span> {totalRegistered}
      </Badge>
      <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 text-sm py-1.5">
        <span className="font-normal text-green-500 dark:text-green-400 mr-1">Đã điểm danh:</span> {totalAttended}
      </Badge>
    </motion.div>
  );
  
  useEffect(() => {
    return () => {
      setSearchQuery('');
    };
  }, [isMobile, setSearchQuery]);
  
  // Helper function to get effective attendees list with local updates applied
  const getEffectiveAttendees = useCallback(() => {
    if (Object.keys(localAttendees).length === 0) {
      return searchQuery && elasticResults.length > 0 ? elasticResults : attendees;
    }
    
    // Start with the appropriate base list
    const baseList = searchQuery && elasticResults.length > 0 ? elasticResults : attendees;
    
    // Apply any local updates
    return baseList.map(attendee => {
      if (attendee.code && localAttendees[attendee.code]) {
        return {
          ...attendee,
          ...localAttendees[attendee.code],
          attended: localAttendees[attendee.code].attended,
          checkedInAt: localAttendees[attendee.code].checkedInAt
        };
      }
      return attendee;
    });
  }, [attendees, elasticResults, localAttendees, searchQuery]);
  
  return (
    <div className="space-y-4 md:space-y-6">
      <QrScanner 
        onQrScan={onQrScan} 
        onCodeSubmit={onCodeSubmit}
        onRefreshData={onRefreshData}
      />
      
      {isMobile && (
        <>
          {renderMobileStats()}
          <SearchBar 
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            placeholder="Tìm tên, SĐT, email, mã số..."
            sticky={true}
            attendees={attendees}
            onAttendeeSelect={handleAttendeeSelect}
            lastSearches={lastSearches}
          />
          <MobileTableBody 
            attendees={getEffectiveAttendees()}
            searchQuery={searchQuery}
            onCheckInSuccess={(attendee, timestamp) => {
              if ('vibrate' in navigator) {
                navigator.vibrate([100, 50, 100]);
              }
              
              // Update local state immediately
              setLocalAttendees(prev => ({
                ...prev,
                [attendee.code]: {
                  ...attendee,
                  attended: true,
                  checkedInAt: timestamp
                }
              }));
              
              handleRefresh();
            }}
            onDirectUpdate={enhancedDirectUpdate}
            onSilentUpdate={onSilentUpdate}
            showContribution={true}
            height={scrollAreaHeight}
          />
          
          <SearchBar 
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            placeholder="Tìm người tham dự..."
            floatingOnMobile={true} 
            sticky={false}
            attendees={attendees}
            onAttendeeSelect={handleAttendeeSelect}
            lastSearches={lastSearches}
            onRefreshData={handleRefresh}
          />
        </>
      )}
      
      {!isMobile && (
        <ScrollArea 
          className={`${scrollAreaHeight} border border-gray-100 dark:border-gray-800 rounded-md overflow-auto scrollbar-visible shadow-sm`}
        >
          <AttendeesTable 
            attendees={getEffectiveAttendees()}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onDirectUpdate={enhancedDirectUpdate}
            onSilentUpdate={onSilentUpdate}
            onRefreshData={onRefreshData}
            lastSearches={lastSearches}
          />
        </ScrollArea>
      )}
    </div>
  );
};

export default React.memo(AttendeesContent);
